/**
 * Enable 2.0 design tokens (canonical, from docs/AI Labs Quote.pdf "Design System").
 *
 * The same palette lives as CSS custom properties in src/styles/index.css; this
 * module mirrors them as a typed object so component code that builds inline
 * `style` objects (the established idiom in this app) can reference tokens
 * without stringly-typed `var(--…)` everywhere and without drifting from the
 * spec. Treat this file + index.css as one source of truth.
 *
 * Behavioral hierarchy (quote, Design System 2 of 3):
 *   ~80% Neutral foundation · ~10% Brand (nav/identity only) ·
 *   ~10% Action (primary CTA) · plus priority-tier and semantic status colors.
 */

export const colors = {
  // [1] Neutral foundation (~80% of the UI). Warm grays, never pure white.
  pageBg: '#FAFAF9', // page background — warm white
  surface: '#F4F3F1', // cards, panels, right-context background
  surfaceAlt: '#E7E5E4', // hover, section dividers, inactive backgrounds
  border: '#D6D3D1', // table-row border, input border at rest
  borderStrong: '#A8A29E', // active input border, focused field outlines
  textPrimary: '#1C1917', // body text + field labels
  textSecondary: '#57534E', // supporting labels, metadata, timestamps
  textMuted: '#A8A29E', // placeholder, disabled, helper text

  // [2] Brand — navigation & identity ONLY (~10%).
  brand: '#2E1065', // left-nav background, primary brand surface
  brandActive: '#4C1D95', // active/selected nav item
  brandAccent: '#7C3AED', // section-header accent, in-nav links
  brandTint: '#EDE9FE', // role badges / tags background tint

  // [3] Action — primary CTA layer (~10%).
  action: '#0F766E', // primary CTA + all primary interactive (teal)
  actionHover: '#0D9488', // CTA hover

  // [3] Priority-tier colors (work-queue).
  priorityCritical: '#EA580C', // top of queue
  priorityHigh: '#D97706', // high conversion / high value
  priorityNormal: '#475569', // standard SLA window
  priorityLow: '#9CA3AF', // watching, no action required

  // [4][5] Semantic status / alert.
  success: '#16A34A', // enrollment confirmed, signature captured, closed
  warning: '#D97706', // SLA approaching, ghosting risk
  danger: '#DC2626', // SLA breached, flagged, escalation
  info: '#2563EB', // system notice, new assignment
  neutral: '#71717A', // archived, inactive, not-started

  white: '#FFFFFF',
} as const;

/** Soft background tints for the semantic/priority chips (computed once). */
export const tints = {
  success: '#DCFCE7',
  successText: '#15803D',
  warning: '#FEF3C7',
  warningText: '#92400E',
  danger: '#FEE2E2',
  dangerText: '#991B1B',
  info: '#DBEAFE',
  infoText: '#1E40AF',
  neutral: '#F4F4F5',
  neutralText: '#52525B',
  brand: '#EDE9FE',
  brandText: '#5B21B6',
  action: '#CCFBF1',
  actionText: '#0F766E',
} as const;

export const radius = {
  sm: '4px',
  md: '8px',
  lg: '12px',
  pill: '999px',
} as const;

export const shadow = {
  sm: '0 1px 2px rgba(28, 25, 23, 0.06), 0 1px 3px rgba(28, 25, 23, 0.08)',
  md: '0 2px 4px rgba(28, 25, 23, 0.06), 0 4px 12px rgba(28, 25, 23, 0.08)',
  lg: '0 8px 24px rgba(28, 25, 23, 0.12)',
} as const;

export const space = {
  xs: 4,
  sm: 8,
  md: 16,
  lg: 24,
  xl: 32,
  '2xl': 48,
} as const;

export type ColorToken = keyof typeof colors;

/** Map a priority tier to its color token. */
export type PriorityTier = 'critical' | 'high' | 'normal' | 'low';
export const PRIORITY_COLORS: Record<PriorityTier, string> = {
  critical: colors.priorityCritical,
  high: colors.priorityHigh,
  normal: colors.priorityNormal,
  low: colors.priorityLow,
};

/** Map a semantic status to its chip colors. */
export type SemanticStatus = 'success' | 'warning' | 'danger' | 'info' | 'neutral';
export const STATUS_TINTS: Record<SemanticStatus, { fg: string; bg: string }> = {
  success: { fg: tints.successText, bg: tints.success },
  warning: { fg: tints.warningText, bg: tints.warning },
  danger: { fg: tints.dangerText, bg: tints.danger },
  info: { fg: tints.infoText, bg: tints.info },
  neutral: { fg: tints.neutralText, bg: tints.neutral },
};
